import { Search, FileText, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import BackButton from "../components/BackButton";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import SOPService, {
  type SOPResponse,
  type SOPImageFile,
} from "../services/APIs/SOPService";

const SOPLibrary = () => {
  const organizationId = localStorage.getItem("OrganizationId");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    string | null
  >(null);

  // Helper to format relative time like "2 days ago"
  const formatRelativeTime = (dateString: string) => {
    if (!dateString) {
      return "";
    }
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return "just now";
    }
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    }
    const days = Math.floor(diffInSeconds / 86400);
    if (days < 7) {
      return `${days} ${days === 1 ? "day" : "days"} ago`;
    }
    const weeks = Math.floor(days / 7);
    if (weeks < 4) {
      return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    }
    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months} ${months === 1 ? "month" : "months"} ago`;
    }
    const years = Math.floor(days / 365);
    return `${years} ${years === 1 ? "year" : "years"} ago`;
  };

  // Helper to get file type from sopimage
  const getFileType = (sopimage: string): "PDF" | "DOC" => {
    try {
      const images: SOPImageFile[] = JSON.parse(sopimage);
      if (images && images.length > 0) {
        const fileName = images[0].file_original_name?.toLowerCase() || "";
        if (fileName.endsWith(".pdf")) {
          return "PDF";
        }
        if (fileName.endsWith(".doc") || fileName.endsWith(".docx")) {
          return "DOC";
        }
      }
    } catch (error) {
      console.error("Error parsing sopimage:", error);
    }
    return "DOC"; // Default to DOC
  };

  // Fetch SOPs with React Query
  const {
    data: sopsData = [],
    isLoading: isLoadingSOPs,
    error: sopsError,
    refetch: refetchSOPs,
  } = useQuery({
    queryKey: ["sops", organizationId],
    queryFn: async () => {
      if (!organizationId) {
        return [];
      }
      return await SOPService.getAllSOPs(organizationId);
    },
    enabled: !!organizationId,
    refetchInterval: 60000, // Refetch every minute
    refetchOnWindowFocus: true,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });

  // Fetch SOP counts for sidebar
  const { data: sopCountsData } = useQuery({
    queryKey: ["sop-counts", organizationId],
    queryFn: async () => {
      if (!organizationId) {
        return null;
      }
      return await SOPService.getSOPCounts(organizationId);
    },
    enabled: !!organizationId,
  });

  // Transform SOP data to match document structure
  const allDocuments = sopsData.map((sop: SOPResponse) => {
    const fileType = getFileType(sop.sopimage || "[]");

    // Parse tags - handle both JSON array and comma-separated string
    let tags: string[] = [];
    if (sop.tags) {
      try {
        // Try parsing as JSON first
        const parsed = JSON.parse(sop.tags);
        if (Array.isArray(parsed)) {
          tags = parsed.map(t => String(t).trim()).filter(t => t);
        } else if (typeof parsed === "string") {
          tags = parsed
            .split(",")
            .map(t => t.trim())
            .filter(t => t);
        }
      } catch {
        // If not JSON, treat as comma-separated string
        tags = sop.tags
          .split(",")
          .map(t => t.trim())
          .filter(t => t);
      }
    }

    return {
      id: sop.id,
      type: fileType,
      title: sop.title || "Untitled Document",
      city: sop.category ? `${sop.category}` : "Unknown City",
      uploader: sop.created_by ?? "Unknown User",
      created_by_name: sop.created_by_name ?? sop.created_by ?? "Unknown User",
      uploadDate: formatRelativeTime(sop.created_on),
      description: sop.description || "",
      tags: tags,
      downloads: 0, // Not available in API response
      department: sop.department, // Store department field for filtering
    };
  });

  // Filter documents by search query (title) and selected department
  const documents = useMemo(() => {
    let filtered = allDocuments;

    // Filter by department
    if (selectedDepartmentId !== null) {
      // Find the department name from the counts data
      const departments = sopCountsData?.departments ?? [];
      const selectedDept = departments.find(
        dept => dept.department_id === selectedDepartmentId
      );
      if (selectedDept) {
        filtered = filtered.filter(doc => {
          // Match by department name or department_id
          return (
            doc.department === selectedDept.department_name ||
            doc.department === selectedDepartmentId
          );
        });
      }
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(doc =>
        doc.title.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [allDocuments, searchQuery, selectedDepartmentId, sopCountsData]);

  // Map API response to departments format
  const departments = useMemo(() => {
    const departmentsData = sopCountsData?.departments ?? [];

    if (departmentsData.length === 0) {
      return [
        {
          name: "All Departments",
          count: allDocuments.length,
          active: selectedDepartmentId === null,
          departmentId: null,
        },
      ];
    }

    return departmentsData.map(dept => ({
      name: dept.department_name,
      count: dept.sop_count,
      active:
        selectedDepartmentId === null
          ? dept.department_id === null // "All Departments" is active by default when none selected
          : dept.department_id === selectedDepartmentId,
      departmentId: dept.department_id,
    }));
  }, [sopCountsData, allDocuments.length, selectedDepartmentId]);

  // Map API response to document types format
  const documentTypes = useMemo(() => {
    const documentTypesData = sopCountsData?.document_types ?? [];

    if (documentTypesData.length === 0) {
      return [
        {
          name: "PDF Policies",
          count: documents.filter(d => d.type === "PDF").length,
          icon: FileText,
        },
        {
          name: "Word Templates",
          count: documents.filter(d => d.type === "DOC").length,
          icon: FileText,
        },
      ];
    }

    return documentTypesData.map(docType => ({
      name:
        docType.document_type === "Word" ? "Word Templates" : "PDF Policies",
      count: docType.count,
      icon: FileText,
    }));
  }, [sopCountsData, documents]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
            <h1 className="text-xl font-bold text-foreground">SOP Library</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar */}
        <aside className="sticky top-[73px] hidden min-h-[calc(100vh-73px)] w-64 border-r border-border bg-sidebar lg:block">
          <div className="space-y-6 p-6">
            {/* Departments */}
            <div>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Departments
              </h2>
              <div className="space-y-1">
                {departments.map(dept => (
                  <button
                    key={dept.name}
                    onClick={() => setSelectedDepartmentId(dept.departmentId)}
                    className={`flex w-full items-center justify-start gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${dept.active
                      ? "bg-primary/10 font-medium text-primary"
                      : "text-foreground hover:bg-muted"
                      }`}
                  >
                    <span className="flex-1 text-left">{dept.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {dept.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Document Type */}
            <div>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Document Type
              </h2>
              <div className="space-y-1">
                {documentTypes.map(type => (
                  <button
                    key={type.name}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted"
                  >
                    <div className="flex items-center gap-2">
                      <type.icon className="h-4 w-4" />
                      <span>{type.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {type.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div>
            {/* Header Section */}
            <div className="mb-6">
              <div className="mb-2">
                <div>
                  <h1 className="mb-1 text-3xl font-bold text-foreground">
                    SOP Library
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Browse, download, and adapt policies from verified cities.
                  </p>
                </div>
              </div>

              {/* Search */}
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search for 'Social Media Policy', 'Snow Removal', 'Hiring'..."
                  className="border-border bg-muted/50 pl-10"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Documents Grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {isLoadingSOPs ? (
                <div className="col-span-2 py-12 text-center text-sm text-muted-foreground">
                  Loading SOPs...
                </div>
              ) : sopsError ? (
                <div className="col-span-2 py-12 text-center text-sm text-destructive">
                  Failed to load SOPs.{" "}
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => refetchSOPs()}
                  >
                    Try again
                  </Button>
                </div>
              ) : documents.length === 0 ? (
                <div className="col-span-2 py-12 text-center text-sm text-muted-foreground">
                  No SOPs found. Share your first SOP to get started!
                </div>
              ) : (
                documents.map(doc => (
                  <Card
                    key={doc.id}
                    className="transition-shadow hover:shadow-md"
                  >
                    <CardContent className="p-5">
                      <div className="mb-3 flex items-start justify-between">
                        <Badge
                          variant={
                            doc.type === "PDF" ? "destructive" : "default"
                          }
                          className="font-semibold"
                        >
                          {doc.type}
                        </Badge>
                        <Badge
                          variant="outline"
                          className="border-primary/20 bg-primary/5 text-primary"
                        >
                          ✓ {doc.city}
                        </Badge>
                      </div>

                      <h3 className="mb-2 font-semibold leading-tight text-foreground">
                        {doc.title}
                      </h3>

                      <p className="mb-3 text-xs text-muted-foreground">
                        Uploaded by {doc.created_by_name} • {doc.uploadDate}
                      </p>

                      <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                        {doc.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {doc.tags.map(tag => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                        {doc.downloads > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {doc.downloads.toLocaleString()} downloads
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-primary"
                          onClick={() => {
                            // Handle download - you can implement this based on your API
                            try {
                              const sop = sopsData.find(
                                (s: SOPResponse) => s.id === doc.id
                              );
                              if (sop?.sopimage) {
                                const images: SOPImageFile[] = JSON.parse(
                                  sop.sopimage
                                );
                                if (
                                  images &&
                                  images.length > 0 &&
                                  images[0].file_path
                                ) {
                                  window.open(images[0].file_path, "_blank");
                                }
                              }
                            } catch (error) {
                              console.error("Error downloading file:", error);
                            }
                          }}
                        >
                          <Download className="mr-1 h-4 w-4" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SOPLibrary;
